import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTenantInfo } from '../features/tenant/tenantSlice';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import httpRequest from '../utils/request';
import { initMercadoPago } from '@mercadopago/sdk-react';
import QRCode from 'qrcode';

const Plans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenant, loading } = useSelector((state) => state.tenant);
  const [pricing, setPricing] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [paymentOptionsModal, setPaymentOptionsModal] = useState({ show: false, bundleType: null, preferenceData: null, method: null });
  const [subscriptionForm, setSubscriptionForm] = useState({ phone: '', comments: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  const pollingInterval = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
    };
  }, []);

  // DIAGNOSTIC: Log component mount
  console.log('🚀 [PLANS] Component mounted - version with logging');
  console.log('🚀 [PLANS] Current URL:', window.location.href);
  console.log('🚀 [PLANS] Search params:', window.location.search);

  // Initialize Mercado Pago SDK with public key from backend
  useEffect(() => {
    const initMP = async () => {
      try {
        const { data } = await httpRequest.get('/mp/public_key');
        if (data.ok && data.publicKey) {
          initMercadoPago(data.publicKey, { locale: 'es-AR' });
          console.log('[MP] Initialized with public key from backend');
        } else {
          console.error('[MP] No se pudo obtener la clave pública de Mercado Pago');
        }
      } catch (err) {
        console.error('[MP] Error al inicializar Mercado Pago:', err);
      }
    };
    initMP();
  }, []);

  useEffect(() => {
    dispatch(getTenantInfo());
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    const fetchPricing = async () => {
      try {
        const { data } = await httpRequest.get('/pricing');
        if (!mounted) return;
        setPricing(data);
      } catch (e) {
        console.warn('No se pudo cargar pricing dinámico, se usan valores por defecto.', e?.message || e);
        if (!mounted) return;
      }
    };
    fetchPricing();
    return () => {
      mounted = false;
    };
  }, []);

  // Handle return from Mercado Pago
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Log ALL parameters for debugging
    const allParams = {};
    for (const [key, value] of params.entries()) {
      allParams[key] = value;
    }
    console.log('[PLANS] URL Parameters:', allParams);

    const paymentStatus = params.get('status');
    const paymentId = params.get('payment_id');
    const collectionId = params.get('collection_id'); // Alternative parameter
    const collectionStatus = params.get('collection_status');
    const externalReference = params.get('external_reference');
    const preferenceId = params.get('preference_id');

    // Use payment_id or collection_id
    const actualPaymentId = paymentId || collectionId;
    const actualStatus = paymentStatus || collectionStatus;

    console.log('[PLANS] Extracted:', {
      actualPaymentId,
      actualStatus,
      externalReference,
      preferenceId
    });

    // Only process if we have payment_id and status is approved
    if (actualPaymentId && actualStatus === 'approved') {
      const verifyPayment = async () => {
        try {
          setProcessingPayment(true);
          console.log('[PLANS] Verifying payment:', actualPaymentId);

          const { data } = await httpRequest.post('/payments/verify-payment', {
            payment_id: actualPaymentId,
            external_reference: externalReference
          });

          console.log('[PLANS] Verification response:', data);

          if (data.ok) {
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);

            // Refresh tenant info
            dispatch(getTenantInfo());

            // Show success message
            alert(data.message || '¡Pago verificado exitosamente! Tu plan ha sido actualizado.');
          } else {
            console.error('[PLANS] Verification failed:', data);
            alert(data.message || 'No se pudo verificar el pago. Contacta a soporte.');
          }
        } catch (err) {
          console.error('[PLANS] Error verifying payment:', err);
          const message = err?.response?.data?.message || err?.message || 'Error al verificar el pago';
          alert(`Error: ${message}. Por favor contacta a soporte.`);
        } finally {
          setProcessingPayment(false);
        }
      };

      verifyPayment();
    } else {
      if (Object.keys(allParams).length > 0) {
        console.log('[PLANS] Not processing payment - missing required params or not approved');
      }
    }
  }, [dispatch]);




  // Handle bundle payment
  // Handle bundle click (opens modal)
  const handleBundleClick = (bundleType) => {
    setPaymentOptionsModal({ show: true, bundleType, preferenceData: null, method: null });
  };

  const handlePaymentMethodSelect = async (method) => {
    if (!tenant || !tenant.id) {
      alert('No se pudo obtener la información del tenant. Recarga la página e intenta nuevamente.');
      return;
    }

    setProcessingPayment(true);
    try {
      const { data } = await httpRequest.post('/payments/create-bundle-payment', {
        tenantId: tenant.id,
        bundleType: paymentOptionsModal.bundleType
      });

      if (data.ok && data.preferenceId) {
        if (method === 'checkout') {
          window.location.href = `https://www.mercadopago.com.ar/checkout/v1/redirect?pref_id=${data.preferenceId}`;
        } else if (method === 'qr') {
          try {
             const qrDataUrl = await QRCode.toDataURL(`https://www.mercadopago.com.ar/checkout/v1/redirect?pref_id=${data.preferenceId}`, { width: 250, margin: 2 });
             setPaymentOptionsModal({ 
               ...paymentOptionsModal,
               method: 'qr',
               preferenceData: { ...data, qrDataUrl } 
             });
             // Start background polling
             if (pollingInterval.current) clearInterval(pollingInterval.current);
             pollingInterval.current = setInterval(() => {
                dispatch(getTenantInfo());
             }, 5000);
          } catch(e) {
             console.error("QR Error", e);
             alert("Error generando código QR");
          }
        }
      } else {
        alert('No se pudo procesar el pago. Intenta nuevamente.');
      }
    } catch (e) {
      const errorData = e?.response?.data;
      const reason = errorData?.error || errorData?.message || e?.message || 'Error desconocido';
      const details = errorData?.details ? JSON.stringify(errorData.details) : '';
      alert(`Error al crear pago de paquete: ${reason} ${details}`);
      console.error('Bundle payment error:', e);
    } finally {
      setProcessingPayment(false);
    }
  };


  // Handle subscription request
  const handleSubscriptionRequest = async () => {
    if (!tenant || !tenant.id) {
      alert('No se pudo obtener la información del tenant. Recarga la página e intenta nuevamente.');
      return;
    }

    setProcessingPayment(true);
    try {
      // Enviar todos los datos del tenant registrados en el sistema
      const { data } = await httpRequest.post('/payments/request-subscription', {
        tenantId: tenant.id,
        // Datos del negocio
        businessName: tenant.businessName || tenant.name,
        subdomain: tenant.subdomain,
        email: tenant.email,
        // Datos fiscales
        razonSocial: tenant.razonSocial,
        cuit: tenant.cuit,
        tipoResponsable: tenant.tipoResponsable,
        // Datos del formulario
        phone: subscriptionForm.phone,
        comments: subscriptionForm.comments,
        // Plan actual
        currentPlan: tenant.plan,
        subscriptionStatus: tenant.subscriptionStatus
      });

      if (data.ok) {
        alert(data.message || 'Solicitud enviada correctamente. Te contactaremos pronto.');
        setShowSubscriptionModal(false);
        setSubscriptionForm({ phone: '', comments: '' });
      }
    } catch (e) {
      const reason = e?.response?.data?.message || e?.message || 'Error desconocido';
      alert('Error al enviar solicitud: ' + reason);
      console.error('Subscription request error:', e);
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatPrice = (currency, amount) => {
    try {
      return new Intl.NumberFormat('es-AR', { style: 'currency', currency: currency || 'ARS', maximumFractionDigits: 0 }).format(amount || 0);
    } catch (_) {
      return `$${amount || 0}`;
    }
  };

  const cfgCurrency = pricing?.currency || 'ARS';
  const monthlyPrice = pricing?.monthlyPaymentPrice || 27999;
  const annualPrice = pricing?.annualPaymentPrice || 299990;
  const ecommerceMonthlyPrice = pricing?.ecommerceMonthlyPrice || 26000;
  const ecommerceAnnualPrice = pricing?.ecommerceAnnualPrice || 260000;
  
  // Bundle prices
  const posShopPrice = pricing?.posShopPrice || 44999;
  const posMeliPrice = pricing?.posMeliPrice || 44999;
  const posTiendanubePrice = pricing?.posTiendanubePrice || 44999;



  if (loading && !tenant) {
    return <Loading />;
  }

  // Styles
  const containerStyle = {
    padding: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '28px',
    paddingTop: '16px'
  };

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '800',
    color: '#2d3748',
    marginBottom: '8px'
  };

  const subtitleStyle = {
    fontSize: '16px',
    color: '#718096',
    marginBottom: '16px'
  };

  const currentPlanBadgeStyle = {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '20px',
    background: '#667eea',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '700',
    marginTop: '12px'
  };

  const paymentOptionsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const paymentCardStyle = (recommended) => ({
    background: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: recommended ? '0 12px 32px rgba(102, 126, 234, 0.2)' : '0 6px 20px rgba(0,0,0,0.1)',
    border: recommended ? '3px solid #667eea' : '2px solid #e2e8f0',
    position: 'relative',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
    ':hover': {
      transform: 'translateY(-4px)'
    }
  });

  const recommendedBadgeStyle = {
    position: 'absolute',
    top: '-12px',
    right: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    padding: '6px 14px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '800',
    textTransform: 'uppercase',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
  };



  const paymentIconStyle = {
    fontSize: '48px',
    marginBottom: '16px',
    display: 'block'
  };

  const paymentTitleStyle = {
    fontSize: '24px',
    fontWeight: '800',
    color: '#2d3748',
    marginBottom: '8px'
  };

  const paymentPriceStyle = {
    fontSize: '36px',
    fontWeight: '800',
    color: '#667eea',
    marginBottom: '4px'
  };

  const paymentPeriodStyle = {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '16px'
  };

  const paymentDescStyle = {
    fontSize: '14px',
    color: '#4a5568',
    marginBottom: '20px',
    lineHeight: '1.6'
  };

  const buttonStyle = (variant = 'primary') => ({
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '700',
    borderRadius: '10px',
    border: 'none',
    cursor: processingPayment ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s',
    background: variant === 'primary'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : variant === 'secondary'
        ? '#f7fafc'
        : '#10b981',
    color: variant === 'secondary' ? '#2d3748' : '#ffffff',
    opacity: processingPayment ? 0.6 : 1,
    boxShadow: variant === 'primary' ? '0 4px 14px rgba(102, 126, 234, 0.4)' : 'none'
  });

  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const modalStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    marginBottom: '16px',
    boxSizing: 'border-box'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>💎 Opciones de Pago</h1>
        <p style={subtitleStyle}>
          Elige la opción que mejor se adapte a tu negocio
        </p>

        {tenant && (
          <div style={currentPlanBadgeStyle}>
            Plan Actual: {tenant.plan?.toUpperCase()} ✨
          </div>
        )}
      </div>

      <div style={paymentOptionsGridStyle}>
        {/* Bundle 1: Sistema POS Only */}
        <div style={paymentCardStyle(false)}>
          <span style={paymentIconStyle}>🏪</span>
          <h3 style={paymentTitleStyle}>Sistema POS</h3>
          <div style={paymentPriceStyle}>{formatPrice(cfgCurrency, monthlyPrice)}</div>
          <div style={paymentPeriodStyle}>por mes</div>
          <p style={paymentDescStyle}>
            Gestión completa de tu local físico. Stock, ventas, AFIP y reportes incluidos.
          </p>
          <button
            style={buttonStyle('primary')}
            onClick={() => handleBundleClick('pos_only')}
            disabled={processingPayment}
          >
            {processingPayment ? 'Procesando...' : '⚡ CONTRATAR POS'}
          </button>
        </div>

        {/* Bundle 2: POS + Tienda Propia */}
        <div style={paymentCardStyle(true)}>
          <div style={recommendedBadgeStyle}>⭐ Más Completo</div>
          <span style={paymentIconStyle}>🌍</span>
          <h3 style={paymentTitleStyle}>POS + Tienda Propia</h3>
          <div style={paymentPriceStyle}>{formatPrice(cfgCurrency, posShopPrice)}</div>
          <div style={paymentPeriodStyle}>por mes (combo ahorro)</div>
          <p style={paymentDescStyle}>
            Tu local + tu propia tienda online sincronizada. Sin comisiones por venta.
          </p>
          <button
            style={buttonStyle('success')}
            onClick={() => handleBundleClick('pos_shop')}
            disabled={processingPayment}
          >
            {processingPayment ? 'Procesando...' : '🚀 ACTIVAR COMBO'}
          </button>
        </div>

        {/* Bundle 3: POS + MeLi */}
        <div style={paymentCardStyle(false)}>
          <span style={paymentIconStyle}>🛍️</span>
          <h3 style={paymentTitleStyle}>POS + Mercado Libre</h3>
          <div style={paymentPriceStyle}>{formatPrice(cfgCurrency, posMeliPrice)}</div>
          <div style={paymentPeriodStyle}>por mes</div>
          <p style={paymentDescStyle}>
            Sincroniza tu stock local con tus publicaciones de Mercado Libre automáticamente.
          </p>
          <button
            style={buttonStyle('primary')}
            onClick={() => handleBundleClick('pos_meli')}
            disabled={processingPayment}
          >
            {processingPayment ? 'Procesando...' : '🔥 VINCULAR MELI'}
          </button>
        </div>

        {/* Bundle 4: POS + Tiendanube */}
        <div style={paymentCardStyle(false)}>
          <span style={paymentIconStyle}>☁️</span>
          <h3 style={paymentTitleStyle}>POS + Tiendanube</h3>
          <div style={paymentPriceStyle}>{formatPrice(cfgCurrency, posTiendanubePrice)}</div>
          <div style={paymentPeriodStyle}>por mes</div>
          <p style={paymentDescStyle}>
            Conecta tu punto de venta físico con tu tienda en Tiendanube de forma simple.
          </p>
          <button
            style={buttonStyle('primary')}
            onClick={() => handleBundleClick('pos_tiendanube')}
            disabled={processingPayment}
          >
            {processingPayment ? 'Procesando...' : '✨ VINCULAR NUBE'}
          </button>
        </div>
      </div>

      <div style={{ ...headerStyle, marginTop: '40px' }}>
        <h2 style={{ ...titleStyle, fontSize: '28px' }}>🔄 Otras Opciones</h2>
        <p style={subtitleStyle}>
          ¿Necesitas algo más específico?
        </p>
      </div>

      <div style={{ ...paymentOptionsGridStyle, gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))' }}>
        {/* Subscription Request Option */}
        <div style={{ ...paymentCardStyle(false), textAlign: 'center' }}>
          <span style={paymentIconStyle}>📬</span>
          <h3 style={paymentTitleStyle}>Suscripción Automática</h3>
          <p style={paymentDescStyle}>
            Olvídate de renovar manualmente. Te enviamos un link de suscripción fija mensual por email.
          </p>
          <button
            style={{ ...buttonStyle('secondary'), maxWidth: '300px', margin: '0 auto' }}
            onClick={() => setShowSubscriptionModal(true)}
            disabled={processingPayment}
          >
            📧 Solicitar Link
          </button>
        </div>
      </div>

      {/* Subscription Request Modal */}
      {showSubscriptionModal && (
        <div style={modalBackdropStyle} onClick={() => setShowSubscriptionModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2d3748' }}>
              Solicitar Suscripción Automática
            </h2>
            <p style={{ color: '#718096', marginBottom: '20px', fontSize: '14px' }}>
              Completa tus datos y te enviaremos el link de suscripción a tu email.
            </p>

            <input
              type="tel"
              placeholder="Teléfono (opcional)"
              style={inputStyle}
              value={subscriptionForm.phone}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, phone: e.target.value })}
            />

            <textarea
              placeholder="Comentarios adicionales (opcional)"
              style={textareaStyle}
              value={subscriptionForm.comments}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, comments: e.target.value })}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                style={{ ...buttonStyle('secondary'), width: 'auto', flex: 1 }}
                onClick={() => setShowSubscriptionModal(false)}
                disabled={processingPayment}
              >
                Cancelar
              </button>
              <button
                style={{ ...buttonStyle('primary'), width: 'auto', flex: 1 }}
                onClick={handleSubscriptionRequest}
                disabled={processingPayment}
              >
                {processingPayment ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Selection Modal */}
      {paymentOptionsModal.show && (
        <div style={modalBackdropStyle} onClick={() => {
           if (pollingInterval.current) clearInterval(pollingInterval.current);
           setPaymentOptionsModal({ show: false, bundleType: null, preferenceData: null, method: null });
        }}>
          <div style={{ ...modalStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2d3748' }}>
              Elegí cómo pagar
            </h2>
            
            {!paymentOptionsModal.preferenceData ? (
              <>
                <p style={{ color: '#718096', marginBottom: '24px', fontSize: '15px' }}>
                  Seleccioná el método de pago que prefieras para continuar.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <button
                    style={{ 
                      ...buttonStyle('success'), 
                      padding: '16px', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      border: 'none',
                      color: 'white'
                    }}
                    onClick={() => handlePaymentMethodSelect('qr')}
                    disabled={processingPayment}
                  >
                    📱 Generar código QR para escanear <span style={{fontSize: '12px', background: '#fff', color: '#059669', padding: '4px 8px', borderRadius: '12px', marginLeft: '8px', verticalAlign: 'middle'}}>Recomendado</span>
                  </button>
                  <button
                    style={{ 
                      ...buttonStyle('secondary'), 
                      padding: '16px', 
                      fontSize: '16px', 
                      background: '#f1f5f9', 
                      color: '#475569',
                      border: '1px solid #cbd5e1'
                    }}
                    onClick={() => handlePaymentMethodSelect('checkout')}
                    disabled={processingPayment}
                  >
                    💳 Pagar por Web (Checkout Mercado Pago)
                  </button>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <button
                    style={{ background: 'transparent', color: '#718096', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}
                    onClick={() => setPaymentOptionsModal({ show: false, bundleType: null, preferenceData: null, method: null })}
                  >
                    Cancelar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: '#718096', marginBottom: '16px', fontSize: '15px' }}>
                  Escaneá este código QR con la app de Mercado Pago o la cámara de tu celular.
                </p>
                
                <div style={{ background: '#fff', padding: '16px', borderRadius: '16px', display: 'inline-block', border: '2px solid #e2e8f0', marginBottom: '20px' }}>
                  <img src={paymentOptionsModal.preferenceData.qrDataUrl} alt="QR Code" style={{ width: '250px', height: '250px' }} />
                </div>
                
                <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '24px', padding: '0 20px' }}>
                  Una vez que hayas completado el pago, tu cuenta se activará automáticamente en unos segundos.
                </p>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    style={{ ...buttonStyle('secondary'), width: 'auto', flex: 1 }}
                    onClick={() => {
                       if (pollingInterval.current) clearInterval(pollingInterval.current);
                       setPaymentOptionsModal({ show: false, bundleType: null, preferenceData: null, method: null });
                    }}
                  >
                    Cerrar
                  </button>
                  <button
                    style={{ ...buttonStyle('success'), width: 'auto', flex: 1 }}
                    onClick={() => {
                       dispatch(getTenantInfo());
                       if (pollingInterval.current) clearInterval(pollingInterval.current);
                       alert('Actualizando estado de la cuenta...');
                       setPaymentOptionsModal({ show: false, bundleType: null, preferenceData: null, method: null });
                    }}
                  >
                    ✅ Ya pagué
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '48px', paddingBottom: '48px' }}>
        <button
          onClick={() => navigate('/business-settings')}
          style={{
            padding: '14px 28px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '10px',
            border: '2px solid #667eea',
            background: 'transparent',
            color: '#667eea',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
        >
          ← Volver a Configuración
        </button>
      </div>
    </div>
  );
};

export default Plans;
